"use client";

import { useSession } from "@/context/SessionContext";
import { markAsRead } from "@/utils/serverActions/notificationsActions";
import { callQuery, QueryHttpError } from "@/utils/queries/callQuery";
import { createClient } from "@/utils/supabase/client";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

// Read through /api/q instead of as a Server Action, so it does not wait in the queue.
// Without a session the action answered an empty list; the proxy answers 401 now.
const fetchNotifications = async (languageCode: string = "es") => {
  try {
    return await callQuery("notifications", languageCode);
  } catch (error) {
    if (error instanceof QueryHttpError && error.status === 401) return [];
    throw error;
  }
};

export interface Notification {
  id: string;
  profile_id: number;
  type_id: number;
  read: boolean;
  created_at: string;
  message: string;
  display_from_date?: string | null;
  related_entity_type?: string | null;
  related_entity_id?: number | null;
  company_id?: number | null;
  metadata?: Record<string, unknown>;
}

const SYSTEM_NOTIFICATION_ICON = "/images/main-logo.png";
const SYSTEM_NOTIFICATION_SW_PATH = "/notification-sw.js";

const getSystemNotificationTitle = (notification: Notification) => {
  switch (notification.related_entity_type) {
    case "budget":
      return "Nuevo presupuesto";
    case "contract":
      return "Nuevo contrato";
    default:
      return null;
  }
};

interface NotificationsContextType {
  notifications: Notification[];
  loading: boolean;
  error: string | null;
  handleMarkAsRead: (notificationId: string) => Promise<void>;
  refreshNotifications: (retries?: number) => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextType>({
  notifications: [],
  loading: true,
  error: null,
  handleMarkAsRead: async () => {},
  refreshNotifications: async () => {},
});

declare global {
  interface Window {
    orquestiNotificationsDebug?: {
      getState: () => {
        permission: NotificationPermission | "unsupported";
        count: number;
        ids: string[];
        latest: Notification | null;
      };
      refresh: () => Promise<void>;
      notifyLatest: () => Promise<void>;
    };
  }
}

export const useNotificationsContext = () => {
  return useContext(NotificationsContext);
};

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { session } = useSession();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const notificationsRef = useRef<Notification[]>([]);
  const notifiedNotificationIdsRef = useRef<Set<string>>(new Set());
  const hasInitializedNotificationIdsRef = useRef(false);
  const notificationPermissionRequestedRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Keep ref in sync with state
  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);
  const supabase = createClient();
  const languageCode = session?.languageCode || "es";

  // Only load notifications when languageCode changes, not on every render
  // Use a ref to track if we've already loaded to avoid unnecessary re-fetches
  const hasLoadedRef = useRef(false);
  const lastLanguageCodeRef = useRef<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
      return;
    }

    void navigator.serviceWorker
      .register(SYSTEM_NOTIFICATION_SW_PATH)
      .catch((error) => {
        console.error("Error registering notification service worker:", error);
      });
  }, []);

  // Function to manually refresh notifications with retry logic
  // Memoize with useCallback to prevent unnecessary recreations
  const triggerSystemNotification = useCallback(
    async (notification: Notification) => {
      if (typeof window === "undefined" || !("Notification" in window)) {
        return;
      }

      let permission = window.Notification.permission;

      if (
        permission === "default" &&
        !notificationPermissionRequestedRef.current
      ) {
        notificationPermissionRequestedRef.current = true;
        try {
          permission = await window.Notification.requestPermission();
        } catch (error) {
          console.error("Error requesting notification permission:", error);
          return;
        }
      }

      if (permission !== "granted") {
        return;
      }

      const title = getSystemNotificationTitle(notification);
      if (!title) {
        return;
      }

      const notificationOptions = {
        body: notification.message || "Tienes una nueva notificación.",
        icon: SYSTEM_NOTIFICATION_ICON,
        tag: notification.id,
      };

      try {
        if ("serviceWorker" in navigator) {
          const registration = await navigator.serviceWorker.ready;
          await registration.showNotification(title, notificationOptions);
          return;
        }
      } catch (error) {
        console.error(
          "Error showing system notification via service worker:",
          error,
        );
      }

      try {
        const systemNotification = new window.Notification(
          title,
          notificationOptions,
        );

        systemNotification.onclick = () => {
          window.focus();
          systemNotification.close();
        };
      } catch (error) {
        console.error("Error showing system notification:", error);
      }
    },
    [],
  );

  const triggerSystemNotificationIfNeeded = useCallback(
    async (notification: Notification | undefined) => {
      if (!notification) {
        return;
      }

      if (
        notification.related_entity_type !== "budget" &&
        notification.related_entity_type !== "contract"
      ) {
        return;
      }

      if (notifiedNotificationIdsRef.current.has(notification.id)) {
        return;
      }

      notifiedNotificationIdsRef.current.add(notification.id);
      await triggerSystemNotification(notification);
    },
    [triggerSystemNotification],
  );

  useEffect(() => {
    const safeNotifications = Array.isArray(notifications) ? notifications : [];
    const currentIds = new Set(
      safeNotifications.map((notification) => notification.id),
    );

    if (!hasInitializedNotificationIdsRef.current) {
      notifiedNotificationIdsRef.current = currentIds;
      hasInitializedNotificationIdsRef.current = true;
      return;
    }

    const newlyAddedNotifications = safeNotifications.filter((notification) => {
      return (
        !notification.read &&
        !notifiedNotificationIdsRef.current.has(notification.id)
      );
    });

    newlyAddedNotifications.forEach((notification) => {
      void triggerSystemNotificationIfNeeded(notification);
    });

    notifiedNotificationIdsRef.current = currentIds;
  }, [notifications, triggerSystemNotification]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.dispatchEvent(
      new CustomEvent("orquesti:notifications-debug", {
        detail: {
          permission:
            "Notification" in window
              ? window.Notification.permission
              : "unsupported",
          count: notifications.length,
          latestNotificationId:
            notifications.length > 0 ? (notifications[0]?.id ?? null) : null,
        },
      }),
    );
  }, [notifications]);

  const refreshNotifications = useCallback(
    async (retries = 3) => {
      for (let attempt = 1; attempt <= retries; attempt++) {
        try {
          // Refreshing notifications
          setLoading(true);
          const data = await fetchNotifications(languageCode);
          const safeData = Array.isArray(data) ? data : [];
          setNotifications(safeData);
          setError(null);
          // Notifications refreshed successfully
          return; // Success, exit the retry loop
        } catch (err) {
          console.error(
            `Error refreshing notifications (attempt ${attempt}/${retries}):`,
            err,
          );
          if (attempt === retries) {
            setError("Failed to refresh notifications");
            notifiedNotificationIdsRef.current = new Set();
            hasInitializedNotificationIdsRef.current = false;
            setNotifications([]);
          } else {
            // Wait before retrying (exponential backoff)
            await new Promise((resolve) => setTimeout(resolve, 200 * attempt));
          }
        } finally {
          if (attempt === retries) {
            setLoading(false);
          }
        }
      }
    },
    [languageCode],
  );

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      process.env.NODE_ENV !== "development"
    ) {
      return;
    }

    window.orquestiNotificationsDebug = {
      getState: () => ({
        permission:
          "Notification" in window
            ? window.Notification.permission
            : "unsupported",
        count: notificationsRef.current.length,
        ids: notificationsRef.current.map((notification) => notification.id),
        latest: notificationsRef.current[0] ?? null,
      }),
      refresh: async () => {
        await refreshNotifications(1);
      },
      notifyLatest: async () => {
        const latestNotification = notificationsRef.current[0];
        if (latestNotification) {
          await triggerSystemNotification(latestNotification);
        }
      },
    };

    return () => {
      delete window.orquestiNotificationsDebug;
    };
  }, [refreshNotifications, triggerSystemNotification]);

  // New function to mark a notification as read
  const handleMarkAsRead = useCallback(
    async (notificationId: string) => {
      try {
        // Marking notification as read

        // Optimistically update the UI first
        setNotifications((prev) => {
          const safePrev = Array.isArray(prev) ? prev : [];
          const updated = safePrev.map((notification) =>
            notification.id === notificationId
              ? { ...notification, read: true }
              : notification,
          );
          console.log(
            "✅ Optimistic update applied. Updated notifications:",
            updated.length,
          );
          return updated;
        });

        // Then update on the server
        const result = await markAsRead(notificationId);
        // Server update successful

        // Refresh notifications to ensure consistency
        // For regular notifications (with receiver_id), the UPDATE event should fire immediately
        // For broadcast notifications (receiver_id = NULL), we need to wait for notification_reads to be committed
        let data = await fetchNotifications(languageCode);
        let safeData = Array.isArray(data) ? data : [];

        // Preserve read status for the notification we just marked as read
        // This prevents it from reappearing due to timing issues
        safeData = safeData.map((n: Notification) =>
          n.id === notificationId ? { ...n, read: true } : n,
        );
        setNotifications(safeData);

        // For broadcast notifications, do a second refresh after a delay
        // For regular notifications, the UPDATE event should handle it, but we do a delayed refresh as backup
        await new Promise((resolve) => setTimeout(resolve, 300));
        data = await fetchNotifications(languageCode);
        safeData = Array.isArray(data) ? data : [];

        // Again, preserve read status for the notification we just marked as read
        safeData = safeData.map((n: Notification) =>
          n.id === notificationId ? { ...n, read: true } : n,
        );
        setNotifications(safeData);
      } catch (err) {
        console.error("❌ Error marking notification as read:", err);
        setError("Failed to mark notification as read");
        // Revert optimistic update on error
        try {
          const data = await fetchNotifications(languageCode);
          const safeData = Array.isArray(data) ? data : [];
          setNotifications(safeData);
        } catch (refreshErr) {
          console.error(
            "❌ Error refreshing notifications after error:",
            refreshErr,
          );
          notifiedNotificationIdsRef.current = new Set();
          hasInitializedNotificationIdsRef.current = false;
          setNotifications([]);
        }
      }
    },
    [languageCode],
  );

  // Initial load of notifications
  useEffect(() => {
    // Skip if languageCode hasn't actually changed
    if (hasLoadedRef.current && lastLanguageCodeRef.current === languageCode) {
      return;
    }

    async function loadNotifications() {
      try {
        setLoading(true);
        const data = await fetchNotifications(languageCode);
        // Ensure data is always an array
        const safeData = Array.isArray(data) ? data : [];
        notifiedNotificationIdsRef.current = new Set(
          safeData.map((notification) => notification.id),
        );
        hasInitializedNotificationIdsRef.current = true;
        setNotifications(safeData);
        setError(null);
        hasLoadedRef.current = true;
        lastLanguageCodeRef.current = languageCode;
      } catch (err) {
        console.error("Error loading notifications:", err);
        setError("Failed to load notifications");
        // Ensure notifications is always an array even on error
        notifiedNotificationIdsRef.current = new Set();
        hasInitializedNotificationIdsRef.current = false;
        setNotifications([]);
        hasLoadedRef.current = true;
        lastLanguageCodeRef.current = languageCode;
      } finally {
        setLoading(false);
      }
    }

    loadNotifications();
  }, [languageCode]);

  // Setup realtime subscriptions
  useEffect(() => {
    if (!session?.profileId) return;

    console.log(
      "Setting up realtime notifications for profile_id:",
      session.profileId,
    );

    // Create a unique channel name to avoid conflicts
    const channelName = `realtime-notifications-${session.profileId}`;

    // Track active timeouts to clean them up
    const activeTimeouts = new Set<NodeJS.Timeout>();

    const channel = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        {
          event: "*", // Listen to INSERT, UPDATE, DELETE
          schema: "public",
          table: "notifications",
          // Note: We don't use a filter here because Supabase Realtime filters
          // don't work reliably with DELETE events (the filter is applied to payload.new
          // which doesn't exist in DELETE events). Instead, we filter in the handler.
        },
        async (payload) => {
          const eventType = payload.eventType;

          // For DELETE events, we only have payload.old
          // For INSERT/UPDATE events, we have payload.new
          // Type assertion for Supabase Realtime payload structure
          const newPayload = payload.new as {
            id?: string | number;
            receiver_id?: number | null;
            related_entity_type?: string | null;
            company_id?: number | null;
          } | null;
          const oldPayload = payload.old as {
            id?: string | number;
            receiver_id?: number | null;
            related_entity_type?: string | null;
            company_id?: number | null;
          } | null;

          const notificationId = newPayload?.id || oldPayload?.id;
          const receiverId =
            newPayload?.receiver_id ?? oldPayload?.receiver_id ?? null;
          const relatedEntityType =
            newPayload?.related_entity_type || oldPayload?.related_entity_type;
          const companyId =
            newPayload?.company_id ?? oldPayload?.company_id ?? null;

          // Log only in development for debugging
          if (process.env.NODE_ENV === "development") {
            console.log("🔔 Notification change detected:", {
              eventType,
              notificationId,
              receiverId: receiverId ?? "NULL (broadcast)",
              companyId,
              relatedEntityType,
              isBudget: relatedEntityType === "budget",
            });
          }

          // For DELETE events with broadcast notifications (receiver_id = NULL),
          // we need to check if the notification exists in local state and if user has access
          if (eventType === "DELETE" && receiverId === null) {
            // This is a broadcast notification DELETE
            // Check if notification exists in local state before processing
            // Use ref to get the latest state
            const notificationExists = notificationsRef.current.some(
              (n) => n.id === notificationId?.toString(),
            );

            // If we have company_id, verify user has access to this company
            if (
              companyId !== null &&
              session.companies &&
              Array.isArray(session.companies)
            ) {
              const userCompanyIds = session.companies.map(
                (c: { id: number }) => c.id,
              );
              if (!userCompanyIds.includes(companyId)) {
                // Ignore DELETE events for notifications from different companies
                return;
              }
            }

            // If notification doesn't exist in local state, refresh from server to catch it
            if (!notificationExists) {
              // Notification not in local state, refresh from server
              const timeout = setTimeout(async () => {
                activeTimeouts.delete(timeout);
                try {
                  const data = await fetchNotifications(languageCode);
                  const safeData = Array.isArray(data) ? data : [];
                  setNotifications(safeData);
                } catch (err) {
                  console.error(
                    "❌ Error refreshing notifications after DELETE:",
                    err,
                  );
                }
              }, 100);
              activeTimeouts.add(timeout);
              return;
            }
          } else {
            // For regular notifications (receiver_id NOT NULL), filter by receiver_id
            if (receiverId && receiverId !== session.profileId) {
              // Ignore events for different users
              return;
            }

            // For INSERT/UPDATE events, we need receiver_id to filter (unless it's a broadcast)
            if (receiverId === null && eventType !== "DELETE") {
              // For INSERT/UPDATE of broadcast notifications, verify company access
              // For budget notifications specifically, we want to refresh even if session.companies is not yet loaded
              // This ensures budget notifications appear immediately when a budget is created
              const isBudgetNotification = relatedEntityType === "budget";

              if (
                companyId !== null &&
                session.companies &&
                Array.isArray(session.companies)
              ) {
                const userCompanyIds = session.companies.map(
                  (c: { id: number }) => c.id,
                );
                if (!userCompanyIds.includes(companyId)) {
                  // Ignore events for notifications from different companies
                  // But allow budget notifications to proceed (they will be filtered by fn_notifications)
                  if (!isBudgetNotification) {
                    return;
                  }
                }
              } else if (companyId === null) {
                console.warn(
                  "⚠️ Event without receiver_id and company_id, skipping:",
                  {
                    eventType,
                    notificationId,
                  },
                );
                // For budget notifications, still proceed to refresh (fn_notifications will filter correctly)
                if (!isBudgetNotification) {
                  return;
                }
              }
              // If we reach here and it's a budget notification, we'll proceed to refresh
              // This ensures budget notifications appear even if session.companies is not yet loaded
            }
          }

          // Processing notification event for current user

          // For DELETE events, update immediately by removing from local state
          // This provides instant feedback without waiting for a server round-trip
          if (eventType === "DELETE") {
            // In DELETE events, we only have payload.old (not payload.new)
            const deletedNotificationId = oldPayload?.id;
            const deletedReceiverId = oldPayload?.receiver_id ?? null;
            const deletedCompanyId = oldPayload?.company_id ?? null;

            // For broadcast notifications (receiver_id = NULL), we already verified access above
            // For regular notifications, verify receiver_id belongs to current user
            if (
              deletedReceiverId !== null &&
              deletedReceiverId !== session.profileId
            ) {
              // Ignore DELETE events for different users
              return;
            }

            if (deletedNotificationId) {
              // Check if notification exists in local state before removing
              // Use ref to get the latest state
              const notificationExists = notificationsRef.current.some(
                (n) => n.id === deletedNotificationId.toString(),
              );

              if (!notificationExists) {
                // Notification not in local state, refresh from server
                // Notification not in local state, refresh from server
                const timeout = setTimeout(async () => {
                  activeTimeouts.delete(timeout);
                  try {
                    const data = await fetchNotifications(languageCode);
                    const safeData = Array.isArray(data) ? data : [];
                    setNotifications(safeData);
                  } catch (err) {
                    console.error(
                      "❌ Error refreshing notifications after DELETE:",
                      err,
                    );
                  }
                }, 100);
                activeTimeouts.add(timeout);
                return;
              }

              // Delete notification from local state immediately

              // Remove from local state immediately
              setNotifications((prev) =>
                Array.isArray(prev)
                  ? prev.filter(
                      (n) => n.id !== deletedNotificationId.toString(),
                    )
                  : [],
              );

              // Refresh from server after a delay to ensure consistency
              // Use a longer delay to account for potential subsequent INSERT events
              // (e.g., when unmarking not_interested, a DELETE happens first, then an INSERT)
              const timeout1 = setTimeout(async () => {
                activeTimeouts.delete(timeout1);
                try {
                  const data = await fetchNotifications(languageCode);
                  const safeData = Array.isArray(data) ? data : [];
                  setNotifications(safeData);
                  // Notifications refreshed after DELETE
                } catch (err) {
                  console.error(
                    `❌ Error refreshing notifications after DELETE:`,
                    err,
                  );
                }
              }, 300);
              activeTimeouts.add(timeout1);

              // Also refresh after a longer delay to catch any subsequent INSERT events
              const timeout2 = setTimeout(async () => {
                activeTimeouts.delete(timeout2);
                try {
                  const data = await fetchNotifications(languageCode);
                  const safeData = Array.isArray(data) ? data : [];
                  setNotifications(safeData);
                  // Notifications refreshed after DELETE (delayed check)
                } catch (err) {
                  console.error(
                    `❌ Error refreshing notifications after DELETE (delayed):`,
                    err,
                  );
                }
              }, 800);
              activeTimeouts.add(timeout2);
            } else {
              // DELETE event without notification ID, refresh from server
              // Fallback: refresh from server with delays to catch any subsequent events
              const timeout1 = setTimeout(async () => {
                activeTimeouts.delete(timeout1);
                try {
                  const data = await fetchNotifications(languageCode);
                  const safeData = Array.isArray(data) ? data : [];
                  setNotifications(safeData);
                  // Notifications refreshed after DELETE (fallback)
                } catch (err) {
                  console.error(
                    `❌ Error refreshing notifications after DELETE (fallback):`,
                    err,
                  );
                }
              }, 300);
              activeTimeouts.add(timeout1);

              const timeout2 = setTimeout(async () => {
                activeTimeouts.delete(timeout2);
                try {
                  const data = await fetchNotifications(languageCode);
                  const safeData = Array.isArray(data) ? data : [];
                  setNotifications(safeData);
                  // Notifications refreshed after DELETE (fallback delayed)
                } catch (err) {
                  console.error(
                    `❌ Error refreshing notifications after DELETE (fallback delayed):`,
                    err,
                  );
                }
              }, 800);
              activeTimeouts.add(timeout2);
            }
            return; // Exit early for DELETE events
          }

          // For UPDATE events (like marking as read), update optimistically
          if (eventType === "UPDATE" && newPayload) {
            const updatedNotification = newPayload as {
              is_read?: boolean;
              receiver_id?: number | null;
              [key: string]: unknown;
            };
            const receiverId =
              updatedNotification.receiver_id ??
              oldPayload?.receiver_id ??
              null;

            // For regular notifications (with receiver_id), verify it's for the current user
            // For broadcast notifications (receiver_id is NULL), we need to check company access
            if (receiverId !== null && receiverId !== session.profileId) {
              // Ignore UPDATE events for different users
              return;
            }

            // For broadcast notifications, verify company access
            if (receiverId === null) {
              const companyId = (updatedNotification.company_id ??
                oldPayload?.company_id) as number | null;
              if (
                companyId !== null &&
                session.companies &&
                Array.isArray(session.companies)
              ) {
                const userCompanyIds = session.companies.map(
                  (c: { id: number }) => c.id,
                );
                if (!userCompanyIds.includes(companyId)) {
                  // Ignore UPDATE events for notifications from different companies
                  return;
                }
              }
            }

            // Update notification in local state
            setNotifications((prev) =>
              Array.isArray(prev)
                ? prev.map((n) =>
                    n.id === notificationId?.toString()
                      ? {
                          ...n,
                          read: updatedNotification.is_read ?? false,
                          // Update any other fields that might have changed
                        }
                      : n,
                  )
                : [],
            );

            // Refresh from server after a short delay to ensure consistency
            const timeout = setTimeout(async () => {
              activeTimeouts.delete(timeout);
              try {
                const data = await fetchNotifications(languageCode);
                const safeData = Array.isArray(data) ? data : [];
                setNotifications(safeData);
                // Notifications refreshed after UPDATE
              } catch (err) {
                console.error(
                  `❌ Error refreshing notifications after UPDATE:`,
                  err,
                );
                // Don't clear notifications on error
              }
            }, 200);
            activeTimeouts.add(timeout);
            return; // Exit early for UPDATE events
          }

          // For INSERT events, refresh after a delay to ensure the database transaction has committed
          // We use multiple refresh stages to handle various timing scenarios
          if (eventType === "INSERT") {
            // INSERT event detected, scheduling refreshes
            // For budget notifications, we need more time because fn_notifications needs to process the broadcast
            const isBudgetNotification = relatedEntityType === "budget";
            const insertedNotificationId = notificationId?.toString();
            const firstRefreshDelay = isBudgetNotification ? 200 : 100;
            const secondRefreshDelay = isBudgetNotification ? 500 : 300;
            const thirdRefreshDelay = isBudgetNotification ? 1200 : 800;

            if (
              isBudgetNotification &&
              process.env.NODE_ENV === "development"
            ) {
              console.log(
                "💰 Budget notification INSERT detected, scheduling refreshes:",
                {
                  notificationId,
                  companyId,
                  firstRefreshDelay,
                  secondRefreshDelay,
                  thirdRefreshDelay,
                },
              );
            }

            // For budget notifications, also do an immediate refresh attempt (very optimistic)
            // This helps catch the notification as soon as possible
            if (isBudgetNotification) {
              // Immediate refresh attempt (very optimistic, might not catch it yet)
              const immediateTimeout = setTimeout(async () => {
                activeTimeouts.delete(immediateTimeout);
                try {
                  const data = await fetchNotifications(languageCode);
                  const safeData = Array.isArray(data) ? data : [];
                  setNotifications(safeData);
                  if (process.env.NODE_ENV === "development") {
                    console.log(
                      "🔄 Budget notification refresh (immediate) completed",
                    );
                  }
                } catch (err) {
                  // Silently fail - this is just an optimistic attempt
                }
              }, 50);
              activeTimeouts.add(immediateTimeout);
            }

            // First refresh: after delay (longer for budget notifications to allow fn_notifications to process)
            const timeout1 = setTimeout(async () => {
              activeTimeouts.delete(timeout1);
              try {
                const data = await fetchNotifications(languageCode);
                const safeData = Array.isArray(data) ? data : [];
                setNotifications(safeData);
                void triggerSystemNotificationIfNeeded(
                  safeData.find((n) => n.id === insertedNotificationId),
                );
                if (
                  isBudgetNotification &&
                  process.env.NODE_ENV === "development"
                ) {
                  console.log(
                    "🔄 Budget notification refresh (first) completed",
                  );
                }
              } catch (err) {
                console.error(
                  `❌ Error refreshing notifications after INSERT (quick):`,
                  err,
                );
              }
            }, firstRefreshDelay);
            activeTimeouts.add(timeout1);

            // Second refresh: after standard delay
            const timeout2 = setTimeout(async () => {
              activeTimeouts.delete(timeout2);
              try {
                const data = await fetchNotifications(languageCode);
                const safeData = Array.isArray(data) ? data : [];
                setNotifications(safeData);
                void triggerSystemNotificationIfNeeded(
                  safeData.find((n) => n.id === insertedNotificationId),
                );
                if (
                  isBudgetNotification &&
                  process.env.NODE_ENV === "development"
                ) {
                  console.log(
                    "🔄 Budget notification refresh (second) completed",
                  );
                }
              } catch (err) {
                console.error(
                  `❌ Error refreshing notifications after INSERT (standard):`,
                  err,
                );
              }
            }, secondRefreshDelay);
            activeTimeouts.add(timeout2);

            // Third refresh: after longer delay (for edge cases where transaction took longer)
            const timeout3 = setTimeout(async () => {
              activeTimeouts.delete(timeout3);
              try {
                const data = await fetchNotifications(languageCode);
                const safeData = Array.isArray(data) ? data : [];
                setNotifications(safeData);
                if (
                  isBudgetNotification &&
                  process.env.NODE_ENV === "development"
                ) {
                  console.log(
                    "🔄 Budget notification refresh (third) completed",
                  );
                }
              } catch (err) {
                console.error(
                  `❌ Error refreshing notifications after INSERT (delayed):`,
                  err,
                );
              }
            }, thirdRefreshDelay);
            activeTimeouts.add(timeout3);
          }
        },
      )
      .subscribe((status) => {
        if (status === "SUBSCRIBED") {
          // Successfully subscribed to notifications realtime
        } else if (status === "CHANNEL_ERROR") {
          console.error("❌ Error subscribing to notifications realtime");
        }
      });

    // Fallback: Periodic refresh to catch any missed realtime events
    // This ensures the UI stays in sync even if realtime fails
    const refreshInterval = setInterval(async () => {
      try {
        const data = await fetchNotifications(languageCode);
        const safeData = Array.isArray(data) ? data : [];
        setNotifications((prev) => {
          const safePrev = Array.isArray(prev) ? prev : [];

          // Create a map of previous notifications by ID for quick lookup
          const prevMap = new Map(safePrev.map((n: Notification) => [n.id, n]));

          // Merge: preserve read status from local state if notification was marked as read locally
          // This prevents read notifications from reappearing due to timing issues with notification_reads
          const merged = safeData.map((newNotif: Notification) => {
            const prevNotif = prevMap.get(newNotif.id);
            // If notification was marked as read locally, always preserve that state
            // This prevents the notification from reappearing if there's a timing issue
            if (prevNotif && prevNotif.read) {
              return { ...newNotif, read: true };
            }
            return newNotif;
          });

          // Check if there are any differences (new notifications, removed ones, or unread status changes)
          const prevIds = new Set(safePrev.map((n: Notification) => n.id));
          const newIds = new Set(merged.map((n: Notification) => n.id));
          const hasChanges =
            safePrev.length !== merged.length ||
            safePrev.some((n: Notification) => !newIds.has(n.id)) ||
            merged.some((n: Notification) => !prevIds.has(n.id)) ||
            // Check if any notification changed from unread to read (server update)
            merged.some((newN: Notification) => {
              const prevN = safePrev.find(
                (n: Notification) => n.id === newN.id,
              );
              return prevN && !prevN.read && newN.read;
            });

          if (hasChanges) {
            return merged;
          }
          return safePrev;
        });
      } catch (err) {
        console.error("❌ Error in periodic notification refresh:", err);
        notifiedNotificationIdsRef.current = new Set();
        hasInitializedNotificationIdsRef.current = false;
        setNotifications([]);
      }
    }, 10000); // Refresh every 10 seconds as fallback

    return () => {
      // Unsubscribing from notifications realtime
      // Clear all active timeouts to prevent memory leaks
      activeTimeouts.forEach((timeout) => clearTimeout(timeout));
      activeTimeouts.clear();
      clearInterval(refreshInterval);
      channel.unsubscribe();
      supabase.removeChannel(channel);
    };
  }, [supabase, languageCode, session?.profileId, session?.companies]);

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        loading,
        error,
        handleMarkAsRead,
        refreshNotifications,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};
